/**
 * <lazy-video> defers a <video> until it is about to scroll into view.
 *
 * The browser fetches a <video>'s poster the moment the element is parsed, so
 * a carousel of videos far below the fold used to download several hundred KB
 * of posters while the LCP image was still loading. The real <video> markup
 * sits in a <template>; until it is needed the element shows a lazily loaded
 * poster <img> with the same aspect ratio, so nothing shifts on swap.
 *
 * Usage:
 *   <lazy-video>
 *     <template>{{ video | video_tag: ... }}</template>
 *     {{ video.preview_image | image_url: width: 800 | image_tag: loading: 'lazy' }}
 *   </lazy-video>
 */
if (!customElements.get('lazy-video')) {
  class LazyVideo extends HTMLElement {
    connectedCallback() {
      const template = this.querySelector(':scope > template');
      if (!template) return;

      if (!('IntersectionObserver' in window)) {
        this.upgrade(template);
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.upgrade(template);
        },
        { rootMargin: '600px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    upgrade(template) {
      this.replaceChildren(template.content.cloneNode(true));
    }
  }

  customElements.define('lazy-video', LazyVideo);
}
